<template>
    <div class="vx-button-switch relative">
        <label v-if="label" class="vs-input--label">{{ label }}</label>
        <div class="switch-background" :class="{ 'border-danger': hasValidationErrors }">
            <div
                ref="selector"
                v-show="localValue !== null"
                class="selector absolute"
                :style="{ 'background-color': selectedColor }"
            />

            <div class="flex flex-nowrap switch-buttons">
                <vx-button
                    v-for="option in options"
                    :ref="getKey(option)"
                    :key="getKey(option)"
                    class="item flex items-center justify-center"
                    color="transparent"
                    @click="select($event, option)"
                >
                    <slot
                        name="button"
                        :option="option"
                        :selected="getKey(localValue) === getKey(option)"
                    >
                        <span
                            class="text-dark"
                            :class="{ selected: getKey(localValue) === getKey(option) }"
                        >
                            {{ getText(option) }}
                        </span>
                    </slot>
                </vx-button>
            </div>
        </div>
        <div :class="{ 'validation-padding': !removeValidationPadding }">
            <transition @before-enter="beforeEnter" @enter="enter" @leave="leave">
                <div
                    key="error"
                    class="con-text-validation span-text-validation-danger vs-input--text-validation-span"
                >
                    <span class="span-text-validation">
                        {{ errorText }}
                    </span>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import _color from '@/assets/utils/color.js'
import validationMixins from '@/plugins/validation/mixins.js'
import { ValidationProvider } from 'vee-validate'

export default {
    name: 'vx-button-switch',
    mixins: [validationMixins],
    components: { ValidationProvider },
    props: {
        options: {
            type: Array,
            default: () => [
                {
                    id: 1,
                    name: 'Left'
                },
                {
                    id: 2,
                    name: 'Middle'
                },
                {
                    id: 3,
                    name: 'Right'
                }
            ]
        },
        value: {
            type: null
        },
        icon: {
            type: String,
            default: null
        },
        label: {
            type: String,
            default: null
        },
        idKey: {
            type: [String, Function],
            default: 'id'
        },
        textKey: {
            type: [String, Function],
            default: 'name'
        },
        valueIsId: {
            type: Boolean,
            default: false
        },
        color: {
            type: String,
            default: 'primary'
        }
    },
    watch: {
        localValue: {
            handler: function (newV, oldV) {
                this.updateSelectorPosition(newV)
            }
        }
    },
    computed: {
        localValue: {
            get() {
                if (this.valueIsId) {
                    return this.options.find((v) => this.getKey(v) === this.value)
                }
                return this.value
            },
            set(v) {
                if (this.valueIsId) {
                    this.$emit('input', this.getKey(v))
                    return
                }
                this.$emit('input', v)
            }
        },
        selectedColor() {
            return _color.getColor(this.color)
        },
        getKey() {
            if (typeof this.idKey === 'string') {
                return (v) => v && v[this.idKey]
            }
            return this.idKey
        },
        getText() {
            if (typeof this.textKey === 'string') {
                return (v) => v && v[this.textKey]
            }
            return this.textKey
        }
    },
    methods: {
        select(e, option) {
            this.localValue = option
        },
        updateSelectorPosition(option) {
            this.$nextTick(() => {
                var items = this.$refs[this.getKey(option)]
                if (!items || !items.any()) {
                    return
                }
                const selector = this.$refs.selector
                const ref = this.getKey(option)
                const selected = this.$refs[ref].first().$el

                if (!option) {
                    selector.style.opacity = 0
                    return
                }

                if (selector && selected) {
                    selector.style.opacity = 1
                    selector.style.width = `${selected.offsetWidth}px`
                    selector.style.left = `${selected.offsetLeft}px`
                    selector.style.height = `${selected.offsetHeight}px`
                }
            })
        },
        beforeEnter(el) {
            el.style.height = 0
        },
        enter(el, done) {
            let h = el.scrollHeight
            el.style.height = h + 'px'
            done()
        },
        leave: function (el) {
            el.style.height = 0 + 'px'
        }
    },
    mounted() {
        this.updateSelectorPosition(this.localValue)
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_variables.scss';

.switch-background {
    position: relative;
    width: 100%;
    height: 40px;
    background-color: $grey-wash;
    color: $light-grey;
    border: 1px solid grey;
}

.selected {
    color: $dark-grey-2 !important;
}

.switch-buttons {
    width: 100%;
    height: 100%;
    background-color: transparent;
    position: absolute;
    z-index: 2;

    .item {
        width: 100%;
        height: 100%;
    }
}

.selector {
    z-index: 1;
    height: 100%;
    transition: all 0.3s ease-in-out;
}

.theme-dark {
    .switch-background {
        background-color: $mid-grey;
        color: $light-grey;
    }
}
</style>
